import { Button, createTheme, MantineColorsTuple } from '@mantine/core'
import classesDoBotao from './Button.module.css'

// Amazonas Inovare — paleta institucional, copiada de docs/DESIGN_SYSTEM.md §2.2.
// Azul #025468 (Pantone 7707 C) | Verde #098d93 (Pantone 7711 C)
// O shade 7 e o `primaryShade` e e o Azul da marca (DS §2.1: "Azul (primaria)").
// Ate 03/10/2026 era #0D9488 ("Teal aprovado FE-01"), um teal fora do manual —
// o DS vence o tema (TASK-12193).
const inovareTeal: MantineColorsTuple = [
  '#e6f3f5',  // 0 — stripe de tabela, hover claro
  '#b3dce2',  // 1 — bordas claras, dividers
  '#80c5cf',  // 2 — badges light bg
  '#4daebc',  // 3 — accent secundario
  '#2a97a9',  // 4 — active state
  '#098d93',  // 5 — Verde da marca (accent)
  '#077a80',  // 6 — hover accent
  '#025468',  // 7 — Azul da marca (primaria)
  '#014354',  // 8 — pressed
  '#012d3a',  // 9 — profundidade maxima
]

/** O botao e o primario cheio do DS §6.1: variante filled (ou nenhuma) e sem cor que nao seja a primaria. */
export function ehPrimarioCheio(primaryColor: string, variant?: string, color?: string): boolean {
  return (variant === undefined || variant === 'filled') && (color === undefined || color === primaryColor)
}

export const theme = createTheme({
  primaryColor: 'inovareTeal',
  primaryShade: 7,
  colors: { inovareTeal },

  fontFamily: '"Barlow", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontFamilyMonospace: '"JetBrains Mono", "Fira Code", monospace',

  headings: {
    fontFamily: '"Barlow", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeight: '700',
  },

  fontSizes: {
    xs: '0.75rem',
    sm: '0.8125rem',
    md: '0.875rem',
    lg: '1rem',
    xl: '1.125rem',
  },

  radius: {
    xs: '3px',
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
  },

  // Color Admin: flat, quase sem sombra — bordas fazem o trabalho
  shadows: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.05)',
    md: '0 2px 8px rgba(0, 0, 0, 0.06)',
    lg: '0 8px 24px rgba(0, 0, 0, 0.08)',
    xl: '0 16px 40px rgba(0, 0, 0, 0.10)',
  },

  components: {
    AppShell: {
      styles: {
        main: {
          backgroundColor: 'var(--ai-body-bg)',
        },
      },
    },
    // Code (inline e block): o default do Mantine tem fundo claro fixo —
    // ilegível no dark (ex.: payload JSON da tela de alertas). Tokens
    // scheme-aware (dark-mode sweep 2026-07-21).
    Code: {
      styles: {
        root: {
          backgroundColor: 'var(--ai-input-bg)',
          color: 'var(--ai-text-primary)',
        },
      },
    },
    Card: {
      defaultProps: { radius: 'md', withBorder: true },
      styles: {
        root: {
          backgroundColor: 'var(--ai-card-bg)',
          borderColor: 'var(--ai-border-light)',
          boxShadow: 'none',
        },
      },
    },
    Paper: {
      defaultProps: { radius: 'md' },
      styles: {
        root: {
          backgroundColor: 'var(--ai-card-bg)',
        },
      },
    },
    Table: {
      defaultProps: { striped: true, highlightOnHover: true },
      styles: {
        th: {
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--ai-text-secondary)',
          fontWeight: 700,
          borderBottomColor: 'var(--ai-border-light)',
        },
        td: {
          borderBottomColor: 'var(--ai-border-light)',
        },
      },
    },
    Button: Button.extend({
      defaultProps: { radius: 'sm' },
      styles: {
        root: {
          fontWeight: 600,
          fontSize: '0.8125rem',
          transition: 'all var(--ai-transition-base)',
        },
      },
      // DS §6.1: o botao primario cheio e o gradiente da marca, nao a cor chapada.
      // O Mantine pinta o root com `background: var(--button-bg)` (shorthand, entao
      // aceita gradiente) e os `vars` do tema entram DEPOIS do varsResolver do
      // componente — por isso sobrescrever so `--button-bg` basta. Fica de fora
      // tudo que nao e o primario cheio: light/outline/subtle/gradient e qualquer
      // `color=` que nao seja o primario puro (red, gray, `inovareTeal.5`...).
      // Desabilitado nao precisa de caso: a regra de :disabled do Mantine usa o
      // shorthand `background` e repinta por cima do gradiente.
      vars: (theme, props) => {
        if (!ehPrimarioCheio(theme.primaryColor, props.variant, props.color)) return { root: {} }
        return { root: { '--button-bg': 'var(--ai-brand-gradient)' } }
      },
      classNames: (theme, props) => ({
        root: ehPrimarioCheio(theme.primaryColor, props.variant, props.color)
          ? classesDoBotao.primarioGradiente
          : undefined,
      }),
    }),
    TextInput: {
      defaultProps: { radius: 'sm' },
      styles: {
        input: {
          borderColor: 'var(--ai-border-medium)',
          backgroundColor: 'var(--ai-input-bg)',
          fontSize: '0.8125rem',
        },
        label: {
          fontWeight: 600,
          fontSize: '0.75rem',
        },
      },
    },
    PasswordInput: {
      defaultProps: { radius: 'sm' },
      styles: {
        input: {
          borderColor: 'var(--ai-border-medium)',
          backgroundColor: 'var(--ai-input-bg)',
        },
        label: { fontWeight: 600, fontSize: '0.75rem' },
      },
    },
    Select: {
      defaultProps: { radius: 'sm' },
      styles: {
        input: {
          borderColor: 'var(--ai-border-medium)',
          backgroundColor: 'var(--ai-input-bg)',
          fontSize: '0.8125rem',
        },
        label: { fontWeight: 600, fontSize: '0.75rem' },
      },
    },
    Textarea: {
      defaultProps: { radius: 'sm' },
      styles: {
        input: {
          borderColor: 'var(--ai-border-medium)',
          backgroundColor: 'var(--ai-input-bg)',
        },
        label: { fontWeight: 600, fontSize: '0.75rem' },
      },
    },
    Badge: {
      defaultProps: { radius: 'sm' },
      styles: {
        root: {
          fontWeight: 700,
          fontSize: '0.6875rem',
          letterSpacing: '0.02em',
          /**
           * O Badge cortava o proprio rotulo: 'ENCER...', 'BAI...', 'ACEIT...' em /chamados e
           * /finops/recomendacoes.
           *
           * A causa nao e a coluna nem o container — diagnostiquei isso errado tres vezes antes
           * de medir. E a combinacao de tres regras do proprio Mantine no root: `inline-grid`,
           * `width: fit-content` e `overflow: hidden` com `text-overflow: ellipsis`. Em celula
           * estreita, `fit-content` resolve para o espaco DISPONIVEL, e o badge obedientemente
           * encolhe abaixo do proprio texto — e ai o ellipsis entra.
           *
           * `min-width: max-content` impede o encolhimento abaixo do conteudo. O badge passa a
           * empurrar a coluna, e a tabela rola. Rotulo de status cortado nao e economia de
           * espaco: 'ENCER...' e 'ENCERRADA' e 'ENCERRANDO' na mesma pintura.
           *
           * Quem quiser truncar de proposito continua podendo — `maw` ou `style` na chamada
           * vencem o tema.
           */
          minWidth: 'max-content',
        },
      },
    },
    Modal: {
      defaultProps: { radius: 'md' },
      styles: {
        title: { fontWeight: 700 },
        content: {
          backgroundColor: 'var(--ai-card-bg)',
          borderColor: 'var(--ai-border-light)',
        },
      },
    },
    NavLink: {
      styles: {
        root: {
          borderRadius: '4px',
          transition: 'all var(--ai-transition-fast)',
        },
      },
    },
    Tabs: {
      styles: {
        tab: {
          fontWeight: 600,
          fontSize: '0.8125rem',
        },
      },
    },
  },
})

// Amazonas Inovare × Color Admin — design tokens
export const aiVars = {
  // Marca
  brandAzul: '#025468',
  brandVerde: '#098d93',
  brandGradient: 'linear-gradient(135deg, #025468 0%, #098d93 100%)',

  // Sidebar — charcoal escuro (Color Admin #2d353c / #20252a)
  sidebarBg: '#2d353c',
  sidebarDeep: '#20252a',
  sidebarText: '#a8b6bc',
  sidebarActiveText: '#ffffff',
  sidebarActiveBg: '#20252a',
  sidebarHoverBg: 'rgba(255, 255, 255, 0.05)',
  sidebarBorder: '#20252a',
  sidebarMenuHeader: '#adb5bd',
  sidebarGlow: '#098d93',

  // Header — Color Admin exact
  headerBg: '#ffffff',
  headerBorder: '#dee2e6',
  // `headerDarkBg`/`headerDarkBorder` sairam em 03/09/2026 (TASK-10330): nao
  // tinham UM consumidor e eram mais uma copia da paleta de dark esperando
  // alguem usar. Quem precisar do header no escuro le `--ai-header-bg`, que e
  // scheme-aware.

  // Superficies
  bodyBg: '#f2f3f4',
  cardBg: '#ffffff',
  inputBg: '#f2f3f4',

  // Texto — Color Admin hierarchy. textSecondary retintado em 22/09/2026
  // (TASK-11185) junto com --ai-text-secondary/--color-text-secondary —
  // terceira copia independente do mesmo papel (consumida por
  // ContainerCard/InstanceCard/OperationLogsViewer/Recursos.tsx via
  // SURFACE.textSecondary), mesmo defeito das outras duas se nao seguisse.
  textPrimary: '#2d353c',
  textSecondary: '#5c636a',
  textMuted: '#adb5bd',
  textInverse: '#ffffff',

  // Semanticas
  successColor: '#2f9e44',
  warningColor: '#f59c1a',
  dangerColor: '#ff5b57',
  infoColor: '#098d93',

  // Bordas — Color Admin exact
  borderLight: '#dee2e6',
  borderMedium: '#ced4da',
  borderDark: '#495057',
}

// TASK-2473: tokens de escopo de chamados (own/org/global) — light + dark.
// Consumidos por ScopeAppliedBadge/ScopeSelector/etc. Os helpers exportados
// permitem injetar no <html> via dark-mode toggle (sem global stylesheet).
export const aiScopeColors = {
  // Light variants
  '--ai-scope-own-bg': 'var(--mantine-color-gray-1)',
  '--ai-scope-own-fg': 'var(--mantine-color-gray-7)',
  '--ai-scope-org-bg': 'var(--mantine-color-indigo-1)',
  '--ai-scope-org-fg': 'var(--mantine-color-indigo-8)',
  '--ai-scope-global-bg': 'var(--mantine-color-grape-1)',
  '--ai-scope-global-fg': 'var(--mantine-color-grape-8)',
  // Dark variants — Color Admin charcoal palette
  '--ai-scope-own-bg-dark': 'var(--mantine-color-dark-5)',
  '--ai-scope-own-fg-dark': 'var(--mantine-color-gray-3)',
  '--ai-scope-org-bg-dark': 'var(--mantine-color-indigo-9)',
  '--ai-scope-org-fg-dark': 'var(--mantine-color-indigo-2)',
  '--ai-scope-global-bg-dark': 'var(--mantine-color-grape-9)',
  '--ai-scope-global-fg-dark': 'var(--mantine-color-grape-2)',
} as const
